import { Button, CountryIcon, ISO3166Alpha2Code } from '@platform/components';

export type LinkedInLink = {
  displayText: string;
  url: string;
};

// TODO: Punting on this as a very large % of LinkedIn URLs are not in the format we expect
// and have no human-readable name
export function getLinkedInLink(profileUrl: string): LinkedInLink {
  const formattedUrl = profileUrl.startsWith('http') ? profileUrl : `https://${profileUrl}`;
  return {
    displayText: 'Profile',
    url: formattedUrl,
  };

  // let url = profileUrl;

  // if (profileUrl.endsWith('/')) {
  //   url = profileUrl.slice(0, -1);
  // }

  // if (url.indexOf('/company/') !== -1) {
  //   return {
  //     displayText: url.substring(url.indexOf('/company/') + 9),
  //     url,
  //   };
  // }
}

export type InfoCardItem =
  | {
      type: 'website';
      url: string;
    }
  | {
      type: 'linked-in';
      // TODO: URL or account name?
      accountName: LinkedInLink;
    }
  | {
      type: 'team-size';
      size: string;
    }
  | {
      type: 'established-date';
      date: string;
    }
  | {
      type: 'org-type';
      orgType: string;
    }
  | {
      type: 'location';
      city: string;
      country: string;
      countryCode?: string;
    }
  | {
      type: 'resume';
      url: string;
    };

type TeamSizeDescription =
  | '0-2 founders only'
  | '3-10 persons'
  | '11-20 persons'
  | '21-50 persons'
  | '51-100 persons'
  | '100+ persons';

/** Currently, the organization_sizes table has description values like '0-2 founders only', '3-10 persons', etc.
 * This function converts those into specific text for these directory pages. This is brittle and might be better solved
 * by replacing that table with an enum in organization_details or some other org-describing table. */
const formatTeamSize = (size: string) => {
  switch (size as TeamSizeDescription) {
    case '0-2 founders only':
      return '0-2 (founders only)';
    case '3-10 persons':
      return '3-10';
    case '11-20 persons':
      return '11-20';
    case '21-50 persons':
      return '21-50';
    case '51-100 persons':
      return '51-100';
    case '100+ persons':
      return '100+';
    default:
      return size; // Fallback to the original size if no match found
  }
};

const InfoItemHeader = ({ text }: { text: string }) => {
  return <div className='text-gray-500 text-sm font-medium'>{text}</div>;
};

const ExternalLinkButton = ({ text, url }: { text: string; url: string }) => {
  return (
    <div className='-mt-[3px]'>
      <Button
        variant='link-color'
        href={url.startsWith('http') ? url : `https://${url}`}
        target='_blank'
        size='lg'
        tailIcon='arrow-narrow-up-right'
        additionalClassnames='h-fit'
      >
        {text}
      </Button>
    </div>
  );
};

const InfoItemText = ({ children }: { children: React.ReactNode }) => {
  return <span className='text-md font-medium'>{children}</span>;
};

const InfoItem = ({ item }: { item: InfoCardItem }) => {
  return (
    <div className='flex flex-col gap-2 text-gray-700'>
      {item.type === 'website' && (
        <>
          <InfoItemHeader text='Website' />
          <ExternalLinkButton text={item.url} url={item.url} />
        </>
      )}
      {item.type === 'location' && (
        <>
          <InfoItemHeader text='Location' />
          <div className='flex items-center gap-2'>
            <div className='w-5 h-5 mr-2'>
              <CountryIcon alpha2={item.countryCode as ISO3166Alpha2Code | undefined} />
            </div>
            <InfoItemText>
              {item.city?.length === 0 ? item.countryCode : `${item.city.trim()}, ${item.country}`}
            </InfoItemText>
          </div>
        </>
      )}
      {item.type === 'linked-in' && (
        <>
          <InfoItemHeader text='LinkedIn' />
          <ExternalLinkButton text={item.accountName.displayText} url={item.accountName.url} />
        </>
      )}
      {item.type === 'resume' && (
        <>
          <InfoItemHeader text='Resume' />
          <ExternalLinkButton text='View doc' url={item.url} />
        </>
      )}
      {item.type === 'team-size' && (
        <>
          <InfoItemHeader text='Team size' />
          <InfoItemText>{formatTeamSize(item.size)}</InfoItemText>
        </>
      )}
      {item.type === 'established-date' && (
        <>
          <InfoItemHeader text='Established' />
          <InfoItemText>{item.date}</InfoItemText>
        </>
      )}
      {item.type === 'org-type' && (
        <>
          <InfoItemHeader text='Organization type' />
          <InfoItemText>{item.orgType}</InfoItemText>
        </>
      )}
    </div>
  );
};

export const InfoCard = ({ items }: { items: InfoCardItem[] }) => {
  return (
    <div className='rounded-xl bg-gray-50 px-6 py-5 border border-gray-100 flex flex-col items-baseline sm:flex-row sm:flex-wrap gap-6 sm:gap-x-14 sm:gap-y-6'>
      {items.map((item, index) => (
        <InfoItem key={index} item={item} />
      ))}
    </div>
  );
};
